import { themeEmoji } from '@/content/themeBadges';
import styles from './journey.module.css';

interface ProgressTrackProps {
  /** Position within the current round, 0-based. */
  current: number;
  /** Questions in the current round — not in the whole set. */
  total: number;
  theme: string;
  /** Used to look up the theme's emoji. Purely decorative. */
  questionId: string;
  /** e.g. "The foundations". Omitted where a creator has no round copy. */
  roundLabel?: string;
}

/**
 * Where we are, and what we are talking about.
 *
 * Everything here counts within the round and nothing hints at what lies past
 * it. Ten questions is the thing she agreed to; "4 of 17", or a "round 1 of 2"
 * standing over it, both quietly move the finish line and turn a short, warm
 * exchange into a progress meter with homework attached. The second round is
 * something to be offered once the first one has earned it — see the result
 * page — not something to be braced for from question one.
 *
 * The round's name stays, because naming what this stretch is about is
 * orientation rather than a total. The dots are decoration for sighted people;
 * the accessible progress is the `progressbar` role and its `aria-valuetext`,
 * which say the same thing in words.
 */
export function ProgressTrack({
  current,
  total,
  theme,
  questionId,
  roundLabel,
}: ProgressTrackProps) {
  const valueText = roundLabel
    ? `${roundLabel}: question ${current + 1} of ${total}`
    : `Question ${current + 1} of ${total}`;

  return (
    <div className={styles.progress}>
      {roundLabel ? <p className={styles.roundLabel}>{roundLabel}</p> : null}

      <p className={styles.progressMeta}>
        <span className={styles.themeBadge}>
          <span className={styles.themeBadgeEmoji} aria-hidden="true">
            {themeEmoji(questionId)}
          </span>
          <span className={styles.themeBadgeText}>{theme}</span>
        </span>
        <span className={styles.progressCount}>
          {current + 1} of {total}
        </span>
      </p>

      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={current + 1}
        aria-valuetext={valueText}
      >
        {Array.from({ length: total }, (_, index) => (
          <span
            key={index}
            className={[
              styles.progressStep,
              index < current ? styles.progressStepDone : '',
              index === current ? styles.progressStepCurrent : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
        ))}
      </div>
    </div>
  );
}
